import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  PieChart, 
  BarChart3, 
  Loader2,
  Building2,
  Percent,
  Wallet,
  Target,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  Eye,
  Calculator
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatCompactCurrency = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return formatCurrency(value);
};

const FinancialDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFinancialData();
  }, []);

  const fetchFinancialData = async () => {
    try {
      const response = await dashboardAPI.getFinancial();
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load financial data');
      console.error('Financial data error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No financial data available</p>
      </div>
    );
  }

  const { portfolio_summary, project_financials, expense_by_category, monthly_trends, status_breakdown } = data;

  // Calculate max values for bar chart scaling
  const maxProjectValue = Math.max(...project_financials.map(p => p.estimated_arv), 1);
  const maxExpenseCategory = Math.max(...expense_by_category.map(e => e.amount), 1);

  // Color mapping for expense categories
  const categoryColors = {
    'Material Cost': 'bg-blue-600',
    'Materials': 'bg-blue-500',
    'Labor': 'bg-amber-500',
    'Permits': 'bg-purple-500',
    'Utilities': 'bg-cyan-500',
    'Marketing': 'bg-pink-500',
    'Closing Costs': 'bg-orange-500',
    'Holding Costs': 'bg-teal-500',
    'Appliances': 'bg-indigo-500',
    'Fixtures': 'bg-rose-500',
    'Landscaping': 'bg-green-500',
    'Other': 'bg-slate-500'
  };

  return (
    <div className="space-y-6" data-testid="financial-dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold font-heading tracking-tight">Financial Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Portfolio-wide financial analysis and ROI tracking
          </p>
        </div>
        <Link to="/underwriting">
          <Button className="bg-primary text-primary-foreground rounded-sm">
            <Calculator className="w-4 h-4 mr-2" />
            Analyze New Deal
          </Button>
        </Link>
      </div>

      {/* Portfolio Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border bg-card rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-sm bg-blue-500/20 flex items-center justify-center">
                <Wallet className="w-4 h-4 text-blue-400" />
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Invested</p>
            </div>
            <p className="text-2xl font-bold font-mono">{formatCurrency(portfolio_summary.total_invested)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Across {portfolio_summary.total_projects} projects
            </p>
          </CardContent>
        </Card>

        <Card className="border-border bg-card rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-sm bg-amber-500/20 flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-amber-400" />
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Committed</p>
            </div>
            <p className="text-2xl font-bold font-mono">{formatCurrency(portfolio_summary.total_committed)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatCompactCurrency(portfolio_summary.total_spent)} spent + {formatCompactCurrency(portfolio_summary.pending_costs)} pending
            </p>
          </CardContent>
        </Card>

        <Card className="border-border bg-card rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-sm bg-green-500/20 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-green-400" />
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Portfolio ARV</p>
            </div>
            <p className="text-2xl font-bold font-mono text-green-400">{formatCurrency(portfolio_summary.total_arv)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Combined after-repair value
            </p>
          </CardContent>
        </Card>

        <Card className="border-border bg-card rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${portfolio_summary.potential_profit >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                {portfolio_summary.potential_profit >= 0 ? (
                  <ArrowUpRight className="w-4 h-4 text-green-400" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 text-red-400" />
                )}
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Potential Profit</p>
            </div>
            <p className={`text-2xl font-bold font-mono ${portfolio_summary.potential_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(portfolio_summary.potential_profit)}
            </p>
            <p className={`text-xs mt-1 flex items-center gap-1 ${portfolio_summary.avg_roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              <Percent className="w-3 h-3" />
              {portfolio_summary.avg_roi}% Average ROI
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ROI & Profit Analysis Section */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Project ROI Breakdown */}
        <Card className="border-border bg-card rounded-sm md:col-span-2">
          <CardHeader className="pb-4">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Project ROI Analysis
            </CardTitle>
            <CardDescription>Profit potential by project</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {project_financials.length > 0 ? (
              project_financials.slice(0, 6).map((project, idx) => (
                <div key={project.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant="outline" className={`rounded-sm text-xs ${
                        project.status === 'sold' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                        project.status === 'completed' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                        project.status === 'in-progress' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                        'bg-slate-500/20 text-slate-400 border-slate-500/30'
                      }`}>
                        {project.status}
                      </Badge>
                      <Link 
                        to={`/projects/${project.id}`}
                        className="font-medium text-sm truncate hover:text-primary transition-colors"
                      >
                        {project.name}
                      </Link>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className={`font-mono font-medium ${project.potential_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(project.potential_profit)}
                      </span>
                      <span className={`font-mono text-xs px-2 py-0.5 rounded-sm ${
                        project.roi >= 20 ? 'bg-green-500/20 text-green-400' :
                        project.roi >= 10 ? 'bg-amber-500/20 text-amber-400' :
                        project.roi >= 0 ? 'bg-slate-500/20 text-slate-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {project.roi}% ROI
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-secondary rounded-sm overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          project.roi >= 20 ? 'bg-green-500' :
                          project.roi >= 10 ? 'bg-amber-500' :
                          project.roi >= 0 ? 'bg-slate-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(Math.max((project.estimated_arv / maxProjectValue) * 100, 5), 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground font-mono w-20 text-right">
                      {formatCompactCurrency(project.estimated_arv)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Building2 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-sm text-muted-foreground">No projects yet</p>
                <Link to="/projects/new">
                  <Button variant="outline" size="sm" className="mt-4 rounded-sm">
                    Create First Project
                  </Button>
                </Link>
              </div>
            )}
            
            {project_financials.length > 6 && (
              <Link to="/projects" className="block text-center mt-4">
                <Button variant="ghost" size="sm" className="text-primary">
                  View All {project_financials.length} Projects
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>

        {/* Expense Breakdown */}
        <Card className="border-border bg-card rounded-sm">
          <CardHeader className="pb-4">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <PieChart className="w-5 h-5 text-primary" />
              Expense Breakdown
            </CardTitle>
            <CardDescription>Spending by category</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {expense_by_category.length > 0 ? (
              expense_by_category.slice(0, 6).map((category, idx) => (
                <div key={category.category} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-sm ${categoryColors[category.category] || 'bg-slate-500'}`} />
                      {category.category}
                    </span>
                    <span className="font-mono text-muted-foreground">{formatCurrency(category.amount)}</span>
                  </div>
                  <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
                    <div 
                      className={`h-full ${categoryColors[category.category] || 'bg-slate-500'}`}
                      style={{ width: `${(category.amount / maxExpenseCategory) * 100}%` }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6">
                <DollarSign className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">No expenses recorded</p>
              </div>
            )}
            
            {expense_by_category.length > 0 && (
              <div className="pt-3 border-t border-border mt-4">
                <div className="flex items-center justify-between text-sm font-medium">
                  <span>Total Spent</span>
                  <span className="font-mono">{formatCurrency(portfolio_summary.total_spent)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cash Flow & Status Section */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Monthly Spending Trends */}
        <Card className="border-border bg-card rounded-sm">
          <CardHeader className="pb-4">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Monthly Spending Trends
            </CardTitle>
            <CardDescription>Cash flow over time</CardDescription>
          </CardHeader>
          <CardContent>
            {monthly_trends.length > 0 ? (
              <div className="space-y-3">
                {monthly_trends.map((month, idx) => {
                  const maxMonthly = Math.max(...monthly_trends.map(m => m.amount), 1);
                  const monthName = new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                  return (
                    <div key={month.month} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-14">{monthName}</span>
                      <div className="flex-1 h-6 bg-secondary rounded-sm overflow-hidden relative">
                        <div 
                          className="h-full bg-gradient-to-r from-primary/80 to-primary transition-all"
                          style={{ width: `${(month.amount / maxMonthly) * 100}%` }}
                        />
                        <span className="absolute inset-0 flex items-center px-2 text-xs font-mono">
                          {formatCurrency(month.amount)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <BarChart3 className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">No spending data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Portfolio Status */}
        <Card className="border-border bg-card rounded-sm">
          <CardHeader className="pb-4">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              Portfolio Status
            </CardTitle>
            <CardDescription>Projects by stage</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {[
                { key: 'planning', label: 'Planning', color: 'bg-slate-500', textColor: 'text-slate-400' },
                { key: 'in-progress', label: 'In Progress', color: 'bg-amber-500', textColor: 'text-amber-400' },
                { key: 'completed', label: 'Completed', color: 'bg-blue-500', textColor: 'text-blue-400' },
                { key: 'sold', label: 'Sold', color: 'bg-green-500', textColor: 'text-green-400' }
              ].map(status => (
                <div key={status.key} className="p-3 rounded-sm bg-secondary/30 border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${status.color}`} />
                    <span className="text-xs text-muted-foreground uppercase">{status.label}</span>
                  </div>
                  <p className={`text-2xl font-bold font-mono ${status.textColor}`}>
                    {status_breakdown.counts[status.key]}
                  </p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {formatCompactCurrency(status_breakdown.values[status.key])} invested
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Project Table */}
      {project_financials.length > 0 && (
        <Card className="border-border bg-card rounded-sm">
          <CardHeader className="pb-4">
            <CardTitle className="font-heading text-lg">Detailed Project Financials</CardTitle>
            <CardDescription>Complete breakdown of all projects</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 text-xs text-muted-foreground uppercase">Project</th>
                    <th className="text-right p-3 text-xs text-muted-foreground uppercase">Purchase</th>
                    <th className="text-right p-3 text-xs text-muted-foreground uppercase">Spent</th>
                    <th className="text-right p-3 text-xs text-muted-foreground uppercase">ARV</th>
                    <th className="text-right p-3 text-xs text-muted-foreground uppercase">Profit</th>
                    <th className="text-right p-3 text-xs text-muted-foreground uppercase">ROI</th>
                    <th className="text-center p-3 text-xs text-muted-foreground uppercase">Budget</th>
                    <th className="text-center p-3 text-xs text-muted-foreground uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {project_financials.map((project) => (
                    <tr key={project.id} className="border-b border-border/50 hover:bg-secondary/20 transition-colors">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`rounded-sm text-xs ${
                            project.status === 'sold' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                            project.status === 'completed' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                            project.status === 'in-progress' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                            'bg-slate-500/20 text-slate-400 border-slate-500/30'
                          }`}>
                            {project.status}
                          </Badge>
                          <span className="font-medium truncate max-w-[150px]">{project.name}</span>
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono">{formatCompactCurrency(project.purchase_price)}</td>
                      <td className="p-3 text-right font-mono text-amber-400">{formatCompactCurrency(project.actual_spent)}</td>
                      <td className="p-3 text-right font-mono text-green-400">{formatCompactCurrency(project.estimated_arv)}</td>
                      <td className={`p-3 text-right font-mono font-medium ${project.potential_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCompactCurrency(project.potential_profit)}
                      </td>
                      <td className={`p-3 text-right font-mono ${
                        project.roi >= 20 ? 'text-green-400' :
                        project.roi >= 10 ? 'text-amber-400' :
                        project.roi >= 0 ? 'text-slate-400' :
                        'text-red-400'
                      }`}>
                        {project.roi}%
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-2">
                          <Progress value={Math.min(project.budget_used_pct, 100)} className="h-2 w-16" />
                          <span className="text-xs text-muted-foreground w-10">{project.budget_used_pct}%</span>
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <Link to={`/projects/${project.id}`}>
                          <Button variant="ghost" size="sm" className="rounded-sm h-7">
                            <Eye className="w-3 h-3" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-secondary/30">
                    <td className="p-3 font-semibold">Portfolio Total</td>
                    <td className="p-3 text-right font-mono font-semibold">{formatCompactCurrency(portfolio_summary.total_invested)}</td>
                    <td className="p-3 text-right font-mono font-semibold text-amber-400">{formatCompactCurrency(portfolio_summary.total_spent)}</td>
                    <td className="p-3 text-right font-mono font-semibold text-green-400">{formatCompactCurrency(portfolio_summary.total_arv)}</td>
                    <td className={`p-3 text-right font-mono font-semibold ${portfolio_summary.potential_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatCompactCurrency(portfolio_summary.potential_profit)}
                    </td>
                    <td className={`p-3 text-right font-mono font-semibold ${portfolio_summary.avg_roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {portfolio_summary.avg_roi}%
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {project_financials.length === 0 && (
        <Card className="border-border bg-card rounded-sm">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <BarChart3 className="w-10 h-10 text-primary/50" />
            </div>
            <h3 className="font-semibold text-lg mb-2">No Portfolio Data Yet</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              Create your first project to start tracking your real estate investment portfolio
            </p>
            <Link to="/projects/new">
              <Button className="bg-primary text-primary-foreground rounded-sm">
                Create First Project
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Disclaimer */}
      <p className="text-xs text-muted-foreground text-center">
        Financial projections are estimates based on current data. Actual returns may vary. 
        This is not financial advice - consult professionals before making investment decisions.
      </p>
    </div>
  );
};

export default FinancialDashboardPage;
